import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';

// Thin wrappers around the three onCall Cloud Functions in
// functions/index.js that replace the Firebase client SDK's own
// sendEmailVerification/sendPasswordResetEmail/verifyBeforeUpdateEmail --
// those trigger Firebase's built-in email, sent from a generic shared
// domain with no reputation of its own, which real-world testing showed
// landing in spam almost every time. These route the same kind of
// verification/reset link through info@pumpous.com instead (see
// functions/index.js for the full rationale), while leaving every call
// site's own return-value convention ({success, error}) and UX unchanged.

// httpsCallable throws a FirebaseError with codes like "functions/
// unauthenticated", "functions/already-exists", etc. (note the "functions/"
// prefix the client SDK adds -- the server-side HttpsError codes in
// functions/index.js are bare, e.g. "already-exists"). Only a couple of
// these are ever useful to show verbatim; everything else falls through to
// a generic message, same "never leak a raw error string" convention
// friendlyAuthError.js already established for auth/* codes.
const friendlyCallableError = (error) => {
  switch (error?.code) {
    case "functions/already-exists":
    case "functions/invalid-argument":
    case "functions/failed-precondition":
      return error.message;
    default:
      return "Something went wrong. Please try again.";
  }
};

export const requestVerificationEmail = async () => {
  try {
    await httpsCallable(functions, "sendVerificationEmail")();
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};

export const requestPasswordReset = async (email) => {
  try {
    await httpsCallable(functions, "sendPasswordResetLink")({ email });
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};

export const requestChangeEmailVerification = async (newEmail) => {
  try {
    await httpsCallable(functions, "sendChangeEmailVerification")({ newEmail });
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};

// The four wrappers below back src/components/AuthAction.jsx -- the custom
// in-app page that handles ?action=...&token=... links from the three
// emails above, entirely independent of Firebase's own oobCode/hosted-page
// mechanism (see functions/index.js's long comment on why). "failed-
// precondition" is the one callable error code used throughout these
// Cloud Functions for "this token is missing/wrong-type/expired" -- its
// message is already friendly (see friendlyCallableError's existing
// passthrough), covering every one of those cases with one consistent,
// non-information-leaking message.

export const verifyResetToken = async (token) => {
  try {
    const result = await httpsCallable(functions, "verifyActionToken")({ token, expectedType: "resetPassword" });
    return { success: true, email: result.data.email };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};

export const completePasswordReset = async (token, newPassword) => {
  try {
    await httpsCallable(functions, "completePasswordReset")({ token, newPassword });
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};

export const completeEmailVerification = async (token) => {
  try {
    await httpsCallable(functions, "completeEmailVerification")({ token });
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};

export const completeChangeEmail = async (token) => {
  try {
    await httpsCallable(functions, "completeChangeEmail")({ token });
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyCallableError(error) };
  }
};
